import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { tileUrl, OSM_ATTRIBUTION } from "../utils/map_tiles"

// The Wire's news map: one pin per recent story with a geocode (Research
// resolves region/lat/lng per article; see Wire::Taxonomy.resolve_geo).
// Free OSM raster tiles, rendered client-side from /apps/wire/map.json — the
// same JSON fills the story rail, so the list and the pins can't drift apart.
//
// The rail's collapse is the shared sidebar-collapse controller on the same
// element; this one owns the map, the list and the tour.
export default class extends Controller {
  static targets = ["map", "list", "count", "playToggle", "playIcon", "playLabel"]
  static values = {
    url: String,
    interval: { type: Number, default: 5000 }
  }

  async connect() {
    if (!this.hasMapTarget) return

    this._markers = new Map()
    this._stories = []
    this._index = -1
    this._timer = null
    // Auto-moving content is only acceptable because it's pausable; someone who
    // asked for less motion gets the map still and plays it themselves.
    this._reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this._map = L.map(this.mapTarget, { zoomControl: true, worldCopyJump: true })
    L.tileLayer(tileUrl(), {
      maxZoom: 18,
      attribution: OSM_ATTRIBUTION
    }).addTo(this._map)
    this._map.setView([25, 0], 2)

    // Any hands-on gesture wins over the tour.
    this.mapTarget.addEventListener("pointerdown", this._pauseFromUser)

    // Leaflet only paints the size it last measured, so collapsing the rail
    // (or any other reflow) leaves an unpainted strip until it re-measures.
    this._resizeObserver = new ResizeObserver(() => {
      if (this._resizeFrame) window.cancelAnimationFrame(this._resizeFrame)
      this._resizeFrame = window.requestAnimationFrame(() => this._map?.invalidateSize())
    })
    this._resizeObserver.observe(this.mapTarget)

    try {
      const response = await fetch(this.urlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!response.ok) throw new Error("map data failed")
      const payload = await response.json()
      this._stories = (Array.isArray(payload) ? payload : []).filter((s) => s.lat != null && s.lng != null)
      this._renderStories()
      this._renderList()
      if (this._stories.length > 1 && !this._reducedMotion) this.play()
      else this._syncPlayButton()
    } catch (_error) {
      this._renderEmpty("The map could not load.")
    }
  }

  disconnect() {
    this._clearTimer()
    this.mapTarget.removeEventListener("pointerdown", this._pauseFromUser)
    this._resizeObserver?.disconnect()
    if (this._resizeFrame) window.cancelAnimationFrame(this._resizeFrame)
    if (this._map) {
      this._map.remove()
      this._map = null
    }
  }

  // --- tour ---------------------------------------------------------------

  togglePlay() {
    this._timer ? this.pause() : this.play()
  }

  play() {
    if (this._stories.length === 0) return

    this._clearTimer()
    this._timer = window.setInterval(() => this._advance(), this.intervalValue)
    this._syncPlayButton()
    this._advance()
  }

  pause() {
    this._clearTimer()
    this._syncPlayButton()
  }

  // Bound so removeEventListener gets the same reference back.
  _pauseFromUser = () => {
    if (this._timer) this.pause()
  }

  _advance() {
    if (this._stories.length === 0) return

    this._index = (this._index + 1) % this._stories.length
    this._focus(this._index, { fromTour: true })
  }

  _clearTimer() {
    if (this._timer) {
      window.clearInterval(this._timer)
      this._timer = null
    }
  }

  _syncPlayButton() {
    if (!this.hasPlayToggleTarget) return

    const playing = Boolean(this._timer)
    if (this.hasPlayIconTarget) {
      this.playIconTarget.className = playing ? "bi bi-pause-fill" : "bi bi-play-fill"
    }
    if (this.hasPlayLabelTarget) {
      this.playLabelTarget.textContent = playing ? "Pause tour" : "Play tour"
    }
    this.playToggleTarget.disabled = this._stories.length === 0
  }

  // --- selection ----------------------------------------------------------

  select(event) {
    const index = Number(event.currentTarget.dataset.storyIndex)
    if (Number.isNaN(index)) return

    this.pause()
    this._index = index
    this._focus(index, { fromTour: false })
  }

  _focus(index, { fromTour }) {
    const story = this._stories[index]
    if (!story) return

    const target = [story.lat, story.lng]
    if (this._reducedMotion) {
      this._map.setView(target, Math.max(this._map.getZoom(), 4), { animate: false })
    } else {
      this._map.flyTo(target, Math.max(this._map.getZoom(), 4), { duration: fromTour ? 1.6 : 0.8 })
    }
    this._markers.get(index)?.openPopup()

    this.listTarget.querySelectorAll(".wire-map-story").forEach((el) => {
      const active = Number(el.dataset.storyIndex) === index
      el.classList.toggle("is-active", active)
      if (active) el.scrollIntoView({ block: "nearest", behavior: this._reducedMotion ? "auto" : "smooth" })
    })
  }

  // --- rendering ----------------------------------------------------------

  _renderStories() {
    if (this._stories.length === 0) return

    const bounds = []
    this._stories.forEach((story, index) => {
      bounds.push([story.lat, story.lng])
      const icon = L.divIcon({
        className: "wire-map-pin-wrap",
        html: '<span class="wire-map-pin"></span>',
        iconSize: [18, 18],
        iconAnchor: [9, 9]
      })
      const marker = L.marker([story.lat, story.lng], { icon: icon, title: story.title })
        .addTo(this._map)
        .bindPopup(this._popupHtml(story))
      marker.on("click", () => {
        this.pause()
        this._index = index
      })
      this._markers.set(index, marker)
    })

    // Defer one frame: the shell may still be laying out when the fetch
    // resolves, and fitBounds against a zero-height container clamps to
    // maxZoom and strands the view.
    requestAnimationFrame(() => {
      this._map.invalidateSize()
      this._map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 })
    })
  }

  _renderList() {
    if (this.hasCountTarget) {
      this.countTarget.textContent = this._stories.length === 1 ? "1 story" : `${this._stories.length} stories`
    }
    if (this._stories.length === 0) {
      this._renderEmpty("No geocoded stories yet.")
      return
    }

    this.listTarget.replaceChildren(...this._stories.map((story, index) => {
      const item = document.createElement("li")
      const button = document.createElement("button")
      button.type = "button"
      button.className = "wire-map-story"
      button.dataset.storyIndex = String(index)
      button.dataset.action = "wire-map#select"

      const title = document.createElement("span")
      title.className = "wire-map-story-title"
      title.textContent = story.title
      button.appendChild(title)

      const meta = document.createElement("span")
      meta.className = "wire-map-story-meta"
      meta.textContent = [story.region, story.published_on].filter(Boolean).join(" · ")
      button.appendChild(meta)

      item.appendChild(button)
      return item
    }))
  }

  _renderEmpty(message) {
    const item = document.createElement("li")
    item.className = "wire-map-stories-empty"
    item.textContent = message
    this.listTarget.replaceChildren(item)
    this._syncPlayButton()
  }

  _popupHtml(story) {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
    const thumb = story.thumb ? `<img class="wire-map-popup-thumb" src="${esc(story.thumb)}" alt="">` : ""
    const meta = [story.region, story.published_on].filter(Boolean).map(esc).join(" · ")
    return `
      <div class="wire-map-popup">
        ${thumb}
        <a class="wire-map-popup-title" href="${esc(story.path)}">${esc(story.title)}</a>
        ${meta ? `<div class="wire-map-popup-meta">${meta}</div>` : ""}
      </div>`
  }
}
